import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCircle, Plus, IdentificationCard, X as XIcon, UploadSimple, FilePdf } from "@phosphor-icons/react";
import RowActions from "@/components/RowActions";
import FileImage from "@/components/FileImage";
import { uploadFile } from "@/lib/files";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function downloadPdf(url, filename) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error("Gagal unduh PDF");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
}

const EMPTY = { nama: "", jabatan: "", divisi: "", halaqah_id: "", foto_url: "", no_hp: "", email: "", status: "aktif" };
const DIVISI_OPTIONS = ["keibadahan", "lugho", "kedisiplinan", "kebersihan", "keamanan", "kesehatan", "tahfiz", "humas", "logistik"];

export default function UstazList() {
  const [items, setItems] = useState([]);
  const [halaqahs, setHalaqahs] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [cardModal, setCardModal] = useState(null);  // {qr, barcode, ustaz}

  const load = async () => {
    try {
      const { data } = await api.get("/ustaz", { params: { q: q || undefined } });
      setItems(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { api.get("/master/halaqah").then((r) => setHalaqahs(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const onFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Foto maks 5MB");
    if (!file.type.startsWith("image/")) return toast.error("File harus gambar");
    try {
      const res = await uploadFile(file, "foto_ustaz");
      setForm((f) => ({ ...f, foto_url: res.url }));
      toast.success("Foto terupload");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload foto");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/ustaz/${editingId}`, form);
        toast.success("Ustaz/Ustazah diperbarui");
      } else {
        await api.post("/ustaz", form);
        toast.success("Ustaz/Ustazah ditambahkan");
      }
      setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      nama: u.nama || "", jabatan: u.jabatan || "", divisi: u.divisi || "",
      halaqah_id: u.halaqah_id || "", foto_url: u.foto_url || "",
      no_hp: u.no_hp || "", email: u.email || "", status: u.status || "aktif",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (u) => {
    await api.delete(`/ustaz/${u.id}`);
    toast.success(`Dihapus: ${u.nama}`);
    load();
  };

  const openIdCard = async (u) => {
    try {
      const { data } = await api.get(`/ustaz/${u.id}/id-card`);
      setCardModal(data);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Sumber Daya</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Data Ustaz / Ustazah</h1>
        <p className="text-sm text-slate-500 mt-1">Terpisah dari data pengguna aplikasi. Dilengkapi QR & Barcode untuk absensi.</p>
      </div>

      <form onSubmit={submit} className="imsyaf-card p-5 space-y-3">
        <div className="grid md:grid-cols-4 gap-3">
          <div><label className="text-xs">Nama *</label>
            <input required className="imsyaf-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="ustaz-nama" /></div>
          <div><label className="text-xs">Jabatan</label>
            <input className="imsyaf-input" placeholder="Ustaz Halaqah, Kepala Divisi…" value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} data-testid="ustaz-jabatan" /></div>
          <div><label className="text-xs">Divisi</label>
            <select className="imsyaf-input" value={form.divisi} onChange={(e) => setForm({ ...form, divisi: e.target.value })} data-testid="ustaz-divisi">
              <option value="">— Pilih —</option>
              {DIVISI_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select></div>
          <div><label className="text-xs">Halaqah</label>
            <select className="imsyaf-input" value={form.halaqah_id} onChange={(e) => setForm({ ...form, halaqah_id: e.target.value })} data-testid="ustaz-halaqah">
              <option value="">— Tidak Terkait —</option>
              {halaqahs.map((h) => <option key={h.id} value={h.id}>{h.nama}</option>)}
            </select></div>
          <div><label className="text-xs">No HP</label>
            <input className="imsyaf-input" value={form.no_hp} onChange={(e) => setForm({ ...form, no_hp: e.target.value })} data-testid="ustaz-hp" /></div>
          <div><label className="text-xs">Email</label>
            <input type="email" className="imsyaf-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ustaz-email" /></div>
          <div><label className="text-xs">Status</label>
            <select className="imsyaf-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="ustaz-status">
              <option value="aktif">Aktif</option><option value="non-aktif">Non-aktif</option>
            </select></div>
          <div><label className="text-xs">Foto (maks 5MB)</label>
            <input type="file" accept="image/*" className="imsyaf-input" onChange={onFotoUpload} data-testid="ustaz-foto" /></div>
        </div>
        {form.foto_url && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <FileImage src={form.foto_url} alt="preview" className="w-14 h-14 rounded-full object-cover border-2 border-green-100" />
            <button type="button" onClick={() => setForm({ ...form, foto_url: "" })} className="text-red-600 hover:underline">Hapus foto</button>
          </div>
        )}
        <div className="flex gap-2">
          <button className="imsyaf-btn-primary" data-testid="ustaz-save-btn"><Plus size={16}/> {editingId ? "Simpan Perubahan" : "Tambah Ustaz/Ustazah"}</button>
          {editingId && <button type="button" onClick={() => { setForm(EMPTY); setEditingId(null); }} className="imsyaf-btn-outline">Batal Edit</button>}
        </div>
      </form>

      <div className="imsyaf-card p-3 flex items-center gap-3">
        <input placeholder="Cari nama / kode…" className="imsyaf-input flex-1" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ustaz-search" />
        <div className="text-sm text-slate-500">Total: <b>{items.length}</b></div>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-4 py-2 font-semibold w-14"></th>
            <th className="text-left px-4 py-2 font-semibold">Kode</th>
            <th className="text-left px-4 py-2 font-semibold">Nama</th>
            <th className="text-left px-4 py-2 font-semibold">Jabatan</th>
            <th className="text-left px-4 py-2 font-semibold">Divisi</th>
            <th className="text-left px-4 py-2 font-semibold">Halaqah</th>
            <th className="text-left px-4 py-2 font-semibold">Status</th>
            <th className="text-right px-4 py-2 font-semibold">Aksi</th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100" data-testid={`ustaz-row-${u.id}`}>
                <td className="px-4 py-2">
                  {u.foto_url ? <FileImage src={u.foto_url} alt={u.nama} className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full bg-green-100 grid place-items-center"><UserCircle size={22} className="text-[#166534]"/></div>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{u.kode}</td>
                <td className="px-4 py-2 font-medium">{u.nama}</td>
                <td className="px-4 py-2 text-xs">{u.jabatan || "-"}</td>
                <td className="px-4 py-2 text-xs">{u.divisi || "-"}</td>
                <td className="px-4 py-2 text-xs">{u.halaqah_nama || "-"}</td>
                <td className="px-4 py-2">
                  {u.status === "aktif"
                    ? <span className="imsyaf-badge bg-green-100 text-green-800">Aktif</span>
                    : <span className="imsyaf-badge bg-slate-100 text-slate-600">Non-aktif</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => openIdCard(u)} title="Kartu QR/Barcode"
                      className="p-1.5 rounded text-slate-600 hover:bg-green-50 hover:text-[#14532d]" data-testid={`ustaz-card-${u.id}`}>
                      <IdentificationCard size={14}/>
                    </button>
                    <RowActions testid={`ustaz-${u.id}`} onEdit={() => startEdit(u)} onDelete={() => del(u)} confirmText="Hapus ustaz/ustazah?" />
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Belum ada data.</td></tr>}
          </tbody>
        </table>
      </div>

      {cardModal && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="ustaz-card-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full relative">
            <button onClick={() => setCardModal(null)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700">
              <XIcon size={18}/>
            </button>
            <div className="text-center">
              {cardModal.ustaz.foto_url && <FileImage src={cardModal.ustaz.foto_url} alt="" className="w-20 h-20 rounded-full mx-auto object-cover ring-2 ring-green-200 mb-3"/>}
              <div className="font-display text-xl font-bold text-[#064E3B]">{cardModal.ustaz.nama}</div>
              <div className="text-xs text-slate-500 mt-0.5">{cardModal.ustaz.jabatan} · {cardModal.ustaz.divisi || "-"}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">{cardModal.ustaz.kode}</div>
              <img src={cardModal.qr_data_url} alt="QR" className="w-40 h-40 mx-auto mt-4" data-testid="ustaz-modal-qr"/>
              <img src={cardModal.barcode_data_url} alt="Barcode" className="w-64 mx-auto mt-2" data-testid="ustaz-modal-barcode"/>
              <div className="flex gap-2 justify-center mt-4">
                <button onClick={() => window.print()} className="imsyaf-btn-outline">Cetak Halaman</button>
                <button onClick={() => downloadPdf(
                  `${BACKEND_URL}/api/ustaz/${cardModal.ustaz.id}/id-card.pdf`,
                  `kartu_ustaz_${cardModal.ustaz.kode || cardModal.ustaz.id}.pdf`
                ).catch((e) => toast.error(e.message))}
                  className="imsyaf-btn-primary" data-testid="ustaz-download-pdf">
                  <FilePdf size={16}/> Unduh Kartu PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
